import React from 'react';
import Heading from '../../components/Typography/Heading/Heading';
import SubHeading from '../../components/Typography/SubHeading/SubHeading';
import PhoneNumber from '../../components/PhoneNumber/PhoneNumber';
import { MFBeaconButton } from '../../components/Beacon/BeaconComponents';
import { navigateToExternal } from '../../core/util';
import { getURLTickers, getRetirementTypeDisplay } from '../ProfileScreen/util';
import { navigateToLoginFromChecklist } from './utils';
import styles from './styles.css';

const StepsSection = () => {
  return (
    <>
      <section>
        <div className="checklist-section-title">
          Opening an account takes 5-10 minutes:
        </div>
        <dl>
          <ol>
            <li aria-describedby="retirement-plan-info">
              <dt data-name="1">Provide your date of birth</dt>
              <dd id="retirement-plan-info">
                Get started with a T. Rowe Price Retirement Fund recommendation,
                one set of our target date funds, based on the year you turn 65.
              </dd>
            </li>
            <li aria-describedby="secure-account-info">
              <dt data-name="2">Provide personal details</dt>
              <dd id="secure-account-info">
                We’ll use these to secure and verify your account.
              </dd>
            </li>
            <li aria-describedby="link-bank-info">
              <dt data-name="3">Link a bank to fund your trade</dt>
              <dd id="link-bank-info">
                Trade requests made during market hours are generally processed
                same-day at 4pm ET.
              </dd>
            </li>
          </ol>
        </dl>
      </section>
      <style jsx>{styles}</style>
    </>
  );
};

const ChecklistSection = () => {
  return (
    <>
      <section>
        <div className="checklist-section-title">You will need:</div>
        <ul>
          <li>A minimum of $1,000</li>
          <li>Social Security number</li>
          <li>Permanent U.S. residence</li>
          <li>Bank account details or bank login credentials</li>
        </ul>
      </section>
      <style jsx>{styles}</style>
    </>
  );
};

const getHeaderText = iraAccountType =>
  `Open a ReadyChoice ${getRetirementTypeDisplay(iraAccountType)} account`;

const getSubHeaderText = iraAccountType => {
  return (
    <>
      <p>
        This solution pairs a {getRetirementTypeDisplay(iraAccountType)} with a
        recommendation for a{' '}
        <a
          href="https://www.troweprice.com/personal-investing/campaign/retirement-funds.html"
          target="_blank"
          rel="noopener noreferrer">
          T. Rowe Price Retirement Fund
        </a>
        .
      </p>
      <style jsx>{styles}</style>
    </>
  );
};

const ReadyChoiceChecklistScreen = ({ appState, next }) => {
  const {
    store: {
      authenticated,
      app: { iraAccountType }
    }
  } = appState;

  // multi-fund buy query check
  // capture tickers to pass to InvestmentReviewScreen
  const multiBuyTickers = getURLTickers(window.location.search);
  const redirectToReadyChoicePublicPage = () =>
    navigateToExternal(
      'https://www.troweprice.com/personal-investing/accounts/retirement/ira/readychoice-ira.html'
    );

  const renderChecklistCTAs = () => (
    <div className="button-container">
      {authenticated ? (
        <MFBeaconButton
          variant="primary"
          size="normal"
          id="checklistContinue"
          onClick={next(multiBuyTickers)}
          label="Continue Opening an Account"
        />
      ) : (
        <div className="login-buttons">
          <MFBeaconButton
            variant="primary"
            size="normal"
            id="checklistLogin"
            onClick={() => navigateToLoginFromChecklist('/profile')}
            label="Already a Client? Log In to Continue"
          />
          <MFBeaconButton
            variant="secondary"
            size="normal"
            id="checklistContinueNew"
            onClick={next(multiBuyTickers)}
            label="New to T. Rowe Price? Start Now"
          />
        </div>
      )}
      <MFBeaconButton
        variant="secondary"
        size="normal"
        id="checklistBack"
        onClick={redirectToReadyChoicePublicPage}
        label="Back"
      />

      <style jsx>{styles}</style>
    </div>
  );

  return (
    <div className="checklist-screen">
      <Heading text={getHeaderText(iraAccountType)} />
      <SubHeading text={getSubHeaderText(iraAccountType)} />

      <div className="qv-row">
        <div className="qv-col col-left">
          <StepsSection />
        </div>

        <div className="qv-col col-right">
          <ChecklistSection />
        </div>
      </div>

      {renderChecklistCTAs()}

      <PhoneNumber />
      <style jsx>{styles}</style>
    </div>
  );
};

export default ReadyChoiceChecklistScreen;
